// src/data/stLetterBodies.js
//
// Bob, 2026-10-26: the Secretary & Treasurer's letter assistant must PRODUCE the letter, not only name it. This renders
// each catalogue entry (src\data\stLetterCatalog.js) to finished text from the officer's filled fields.
//
// FORM LETTERS, NOT INVENTED CONTENT (PD-0). Every local-specific value - a fee, a period, a day-count, an amount, a date -
// is a FIELD the officer supplies; the template places it and never assumes it. The fixed language is grounded in each
// entry's stated BASIS (SMART Constitution Art. 21B Secs. 49/63/64; NLRB delinquency-notice requirements; DOL OLMS; the
// real Drive source letters cited in the catalogue). The voice is the office's own: firm, short sentences, accurate, timely.
//
// PURE. No Firestore, no clock (today is passed in via ctx). No import of anything but the catalogue.
import { letter as catalogEntry } from './stLetterCatalog';

const F = (v) => String(v == null ? '' : v).trim();
const or = (v, fb) => F(v) || fb;
const money = (v) => { const n = Number(String(v).replace(/[^0-9.]/g, '')); return Number.isFinite(n) && String(v).trim() !== '' ? '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',') : or(v, '[amount]'); };

// The heading and closing every letter carries. local + signer + date come from ctx; the member/addressee from the body.
function frame(ctx, { re, salute, paragraphs, closing }) {
  const c = ctx || {};
  const head = [
    or(c.platformName, 'SMART Transportation Division'),
    'Local ' + or(c.local, '[Local]'),
    or(c.today, '[date]'),
  ].join('\n');
  const body = (paragraphs || []).map(F).filter(Boolean).join('\n\n');
  const sign = [
    or(closing, 'Fraternally,'),
    '',
    or(c.signerName, '[name]'),
    or(c.signerOffice, 'Secretary & Treasurer') + ', Local ' + or(c.local, '[Local]'),
  ].join('\n');
  return [head, '', (re ? 'Re: ' + re + '\n' : ''), or(salute, 'Dear Sir or Madam:'), '', body, '', sign].join('\n').replace(/\n{3,}/g, '\n\n');
}

// key -> (fields, ctx) -> paragraphs. Each builder reads only the fields that entry's `needs` declares.
const BUILDERS = {
  dues_receipt_annual: (f) => ({
    re: 'Dues receipt - ' + or(f.year, '[year]'),
    salute: 'Dear Brother/Sister ' + or(f.member, '[member]') + ':',
    paragraphs: [
      'This confirms the dues you paid to this Local for the year ' + or(f.year, '[year]') + ': ' + money(f.duesPaid) + '.',
      'Keep this statement for your records. If it does not agree with your own record, tell me at once so the Local’s books can be corrected.',
    ],
  }),
  dues_delinquency: (f) => ({
    re: 'Dues in arrears',
    salute: 'Dear Brother/Sister ' + or(f.member, '[member]') + ':',
    paragraphs: [
      'Our records show your membership dues are in arrears for ' + or(f.monthsOwed, '[months]') + ', in the amount of ' + money(f.amountOwed) + '.',
      'This amount is computed as follows: ' + or(f.howComputed, '[how the amount was computed]') + '.',
      'You must pay this amount in full by ' + or(f.payBy, '[date]') + '. ' + or(f.consequence, 'If it is not paid by that date, [the consequence under the bylaws].'),
      'If you believe this notice is in error, contact me at once and bring your own record of payment.',
    ],
  }),
  suspension_nonpayment: (f) => ({
    re: 'Suspension for non-payment of dues',
    salute: 'Dear Brother/Sister ' + or(f.member, '[member]') + ':',
    paragraphs: [
      'Because your dues have remained unpaid for ' + or(f.monthsOwed, '[months]') + ', you are suspended from membership effective ' + or(f.effectiveDate, '[date]') + ', under the Local’s bylaws.',
      'This is a suspension for non-payment. It is not a charge against you and is not member discipline; discipline is a separate matter of charges and trial under the Constitution, Article 3, Section 3.',
      'You may be reinstated. To do so: ' + or(f.reinstatementPath, '[the reinstatement path under the bylaws]') + '.',
    ],
  }),
  reinstatement_terms: (f) => ({
    re: 'Reinstatement terms',
    salute: 'Dear Brother/Sister ' + or(f.member, '[member]') + ':',
    paragraphs: [
      'To be reinstated to membership in good standing, the following must be paid: back dues of ' + money(f.backDues) + ', and a reinstatement fee of ' + money(f.reinstatementFee) + '.',
      'Payment is due by ' + or(f.payBy, '[date]') + '. On receipt in full, your membership is reinstated and your record updated.',
    ],
  }),
  withdrawal_card: (f) => ({
    re: 'Honorable withdrawal card',
    salute: 'Dear Brother/Sister ' + or(f.member, '[member]') + ':',
    paragraphs: [
      'At your written request, and your dues being current, an honorable withdrawal card is issued to you effective ' + or(f.effectiveDate, '[date]') + '.',
      'Reason of record: ' + or(f.reason, '[reason]') + '.',
      'Keep this card. It preserves your standing for the period the Constitution and the Local’s bylaws allow, and it is surrendered when you return to active service and re-deposit it.',
    ],
  }),
  union_shop_delinquency: (f) => ({
    re: 'FINAL NOTICE - Union Shop Agreement',
    salute: 'Dear Brother/Sister ' + or(f.member, '[member]') + ':',
    paragraphs: [
      'This is a final notice. Your membership dues are in arrears for ' + or(f.monthsOwed, '[months]') + ', in the amount of ' + money(f.amountOwed) + '.',
      'Under the Union Shop Agreement (' + or(f.unionShopCite, '[the controlling Union Shop clause]') + '), continued membership in good standing is a condition of your employment. If this amount is not paid within ' + or(f.cureDays, '[number]') + ' days of the date of this notice, your employment may be placed in jeopardy and the matter referred to the carrier for action under that Agreement.',
      'Pay this amount now and keep your record clear. If you believe this notice is in error, contact me at once.',
    ],
  }),
  dues_deduction_change: (f) => ({
    re: 'Dues check-off - ' + (F(f.action).toLowerCase().indexOf('stop') === 0 ? 'STOP deduction' : or(f.action, '[start/stop]')),
    salute: 'To the Payroll/Dues Deduction Office:',
    paragraphs: [
      'Please ' + or(f.action, '[start/stop]') + ' automatic dues deduction, effective ' + or(f.effectiveDate, '[date]') + ', for the following member(s) of this Local:',
      or(f.members, '[member or list of members]'),
      'This direction is given under the dues check-off authorization on file. Confirm the change in writing to this office.',
    ],
  }),
  new_member_welcome: (f) => ({
    re: 'Membership record established',
    salute: 'Dear Brother/Sister ' + or(f.member, '[member]') + ':',
    paragraphs: [
      'Welcome. Your membership record with this Local is established as of ' + or(f.date, '[date]') + '.',
      'Keep this office advised of your current home address, as the Constitution requires (Article 21B, Section 49), so that every notice reaches you.',
    ],
  }),
  address_update_request: (f) => ({
    re: 'Your address on file',
    salute: 'Dear Brother/Sister ' + or(f.member, '[member]') + ':',
    paragraphs: [
      'Please confirm or correct the home address this Local holds for you.',
      'The Constitution (Article 21B, Section 49) requires each member to keep the Secretary & Treasurer advised of his current home address. An out-of-date address means a notice you are entitled to may not reach you in time.',
      'Reply to this office with your current mailing address.',
    ],
  }),
  nomination_notice: (f) => ({
    re: 'Notice of nominations',
    salute: 'To the members of this Local:',
    paragraphs: [
      'Notice is given that nominations for office will be held on ' + or(f.meetingDate, '[date]') + ', at ' + or(f.place, '[place]') + '.',
      'The positions to be nominated: ' + or(f.positions, '[positions]') + '.',
      'This notice is posted ' + or(f.postedDate, '[date posted]') + '. Confirm the posting period against Article 21B and the Local’s bylaws.',
    ],
  }),
  election_notice: (f) => ({
    re: 'Notice of election',
    salute: 'To the active members of this Local:',
    paragraphs: [
      'Notice is given that an election of officers will be held, with ballots tabulated on ' + or(f.tabulationDate, '[date]') + '.',
      'The positions to be filled: ' + or(f.positions, '[positions]') + '.',
      'This notice is mailed ' + or(f.mailedDate, '[date mailed]') + ' to each active member at the address on file. Confirm the mailing period against Article 21B and the Local’s bylaws.',
    ],
  }),
  meeting_notice_minutes: (f) => ({
    re: (F(f.kind).toLowerCase().indexOf('minute') >= 0 ? 'Minutes - ' : 'Meeting notice - ') + or(f.meetingDate, '[date]'),
    salute: 'To the members of this Local:',
    paragraphs: [
      F(f.kind).toLowerCase().indexOf('minute') >= 0
        ? 'The minutes of the Local meeting held ' + or(f.meetingDate, '[date]') + ' are distributed with this notice for the members’ record.'
        : 'Notice is given of a meeting of this Local on ' + or(f.meetingDate, '[date]') + '. Business before the meeting is as posted; members are asked to attend.',
    ],
  }),
  per_capita_remittance: (f) => ({
    re: 'Per-capita remittance - ' + or(f.month, '[month]'),
    salute: 'To the General Secretary-Treasurer:',
    paragraphs: [
      'Enclosed is this Local’s remittance of monies due the International for the month of ' + or(f.month, '[YYYY-MM]') + ', in the amount of ' + money(f.amount) + '.',
      'This remittance is made under Article 21B, Section 64, not later than the last day of the month.',
    ],
  }),
  withholding_report_ptd: (f) => ({
    re: 'Withholding of a disbursement - Section 64',
    salute: 'To the President, Transportation Division:',
    paragraphs: [
      'This reports, at once, a payment this Local approved that I have withheld under Article 21B, Section 64.',
      'The disbursement: ' + or(f.disbursement, '[the disbursement withheld]') + '. The provision it would violate: ' + or(f.provision, '[the Constitution or bylaw provision]') + '. The reason: ' + or(f.reason, '[the reason]') + '.',
      'Payment is withheld for a period not to exceed thirty days, and I will be governed by your instruction, subject to appeal.',
    ],
  }),
  trustees_report_feb: (f) => ({
    re: 'Annual report to the Board of Trustees - ' + or(f.year, '[year]'),
    salute: 'To the Board of Trustees of this Local:',
    paragraphs: [
      'Submitted with this cover, in duplicate on the prescribed form, is the report of this Local’s receipts and disbursements for the year ' + or(f.year, '[year]') + '.',
      'This report is submitted during February under Article 21B, Section 64.',
    ],
  }),
  signed_report_90th: (f) => ({
    re: 'Signed annual report - fiscal year ending ' + or(f.fiscalYearEnd, '[date]'),
    salute: 'To the General Secretary-Treasurer and the President, Transportation Division:',
    paragraphs: [
      'Enclosed is the signed annual report of this Local for the fiscal year ending ' + or(f.fiscalYearEnd, '[date]') + '.',
      'It is sent under Article 21B, Section 64, no later than the ninetieth day after the fiscal year ends.',
    ],
  }),
  dues_increase_notice: (f) => ({
    re: 'Notice of dues increase',
    salute: 'To the members of this Local, and to the General Secretary-Treasurer:',
    paragraphs: [
      'Notice is given that this Local’s dues are increased to ' + money(f.amount) + ', effective ' + or(f.effectiveDate, '[date]') + '.',
      'The increase was authorized by ' + or(f.authority, '[the authorizing bylaw/vote]') + '. The result of the vote: ' + or(f.voteResult, '[vote result]') + '.',
      'The General Secretary-Treasurer is notified of this change as required.',
    ],
  }),
  government_report_cover: (f) => ({
    re: 'Government filing - ' + or(f.what, '[report]'),
    salute: 'To the filing office:',
    paragraphs: [
      'Transmitted with this cover is this Local’s ' + or(f.what, '[report, e.g. DOL OLMS Form LM-3]') + ' for the period ' + or(f.period, '[period]') + '.',
      'It is due ' + or(f.dueDate, '[due date]') + '. Confirm the current form and thresholds with the filing body before submitting; these changed under a 2026 OLMS rule.',
    ],
  }),
};

// Render a letter to finished text. Returns { ok, text, missing } - missing lists the needs fields left blank, so the UI
// can warn (it still renders, with a [bracketed] placeholder, so the officer sees the whole letter before filling).
export function renderLetter(key, fields, ctx) {
  const entry = catalogEntry(key);
  const build = BUILDERS[key];
  if (!entry || !build) return { ok: false, text: '', missing: [], error: 'No such letter.' };
  const f = fields || {};
  const missing = (entry.needs || []).filter((n) => !F(f[n]));
  const parts = build(f, ctx || {});
  const text = frame(ctx || {}, parts);
  return { ok: true, text, missing };
}

// Does every catalogue letter have a body? (A guard for the test, so a new catalogue entry without a body is caught.)
export function hasBody(key) { return !!BUILDERS[key]; }
export const BODY_KEYS = Object.freeze(Object.keys(BUILDERS));
